import * as React from "react";
import { observer } from "mobx-react";

import {
  UitkExperimentalButtonTabs,
  UitkTab,
  UitkTabsType,
  UitkTabAnchorType,
} from "uitk-react-experimental-button-tabs";

import { useLocalization } from "@shared-ui/localization-context";

import { FlightHiddenSearchDataProps, WizardFlightPWAViewProps, WizardFlightPWAFTNodeOptions } from "../typings";
import { RoundtripFlight } from "../components/RoundtripFlight";
import { OnewayFlight } from "../components/OnewayFlight";
import { MulticityFlight } from "../components/MulticityFlight";
import { LobTitle } from "src/components/shared/LobTitle/LobTitle";
import { FlightHiddenSearchData } from "../components/FlightHiddenSearchData";
import { WizardFlightPWA } from "../WizardFlightPWA";
import { FHCHiddenSearchData } from "../components/FHCHiddenSearchData";
import { LobSubtitle } from "src/components/shared/LobSubtitle/LobSubtitle";
import { locKeys } from "../components/l10n";
import { FlightType } from "src/stores/wizard/state/typings";
import { normalizeOptionsData } from "src/components/flexFramework/FlexTemplateNode";
import { SubmitRow } from "components/shared/StorefrontWizard/SubmitRow/SubmitRow";
import { liveAnnounce, Level } from "uitk-react-utils";
import { Travelers } from "src/components/shared/Travelers/Travelers";
import { PreferredClassInput } from "components/shared/PreferredClassInput/PreferredClassInput";
import { PreferredAirlineInput } from "components/shared/PreferredAirlineInput/PreferredAirlineInput";
import { TravelersTriggerTypes } from "src/components/shared/TravelersField/typings";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "@shared-ui/viewport-context";
import { UitkLayoutConditionalGridSpan } from "uitk-react-layout-grid";
import { StickyRoundtripFlight } from "../components/StickyRoundTripFlight";
import { StickyOnewayFlight } from "../components/StickyOnewayFlight";
import { UitkSheet } from "uitk-react-sheet";
import { UitkPrimaryButton } from "uitk-react-button";
import { UitkSpacing } from "uitk-react-spacing";
import { useRefTargetScroll, useRefContext } from "@shared-ui/ref-context";
import { useDidUpdateEffect } from "src/utils/hooks/useDidUpdateEffect";
import {
  checkIfCurrentLastLegDateUpdated,
  triggerFlightsPredictiveSearchURL,
} from "src/utils/FlightsPredictiveSearchUtils";
import { StackedSticky, useStickyElement } from "src/components/shared/StackedSticky/StackedSticky";

enum ViewSize {
  SMALL = "small",
  MEDIUM = "medium",
}

const MobileCTA = ({
  view,
  ctaVisible,
  onClickScrollToWizard,
  displayText,
}: {
  view: ViewSize;
  ctaVisible: boolean;
  onClickScrollToWizard: () => void;
  displayText: string;
}) => {
  if (view !== ViewSize.SMALL) {
    return null;
  }

  return (
    <UitkSheet className="mobileStickyWizardCTA" isVisible={ctaVisible}>
      <UitkSpacing padding={{ block: "four", inline: "three" }} className="elevation-24">
        <div>
          <UitkLayoutFlex justifyContent="space-between" wrap="nowrap" alignItems="center" space="two">
            <>
              <UitkLayoutFlexItem>
                <UitkPrimaryButton
                  data-testid="MobileStickyCTA"
                  onClick={onClickScrollToWizard}
                  text={displayText}
                  isFullWidth
                />
              </UitkLayoutFlexItem>
            </>
          </UitkLayoutFlex>
        </div>
      </UitkSpacing>
    </UitkSheet>
  );
};

export const WizardFlightPWALPView: React.FunctionComponent<WizardFlightPWAViewProps> = observer((props) => {
  const {
    flightWizardState,
    globalWizardState,
    flightWizardState: { config: flightConfig },
  } = props.wizardState;
  const { flexModuleModelStore, flexTemplateNode } = props;
  const model = flexModuleModelStore.getModel(flexTemplateNode.id);

  const options = normalizeOptionsData(flexTemplateNode, model) as WizardFlightPWAFTNodeOptions;
  const { formatText } = useLocalization();
  const { registerTarget } = useRefContext();
  const { scrollTo } = useRefTargetScroll();
  const [isCTASticky, setCTASticky] = React.useState(false);
  const stickyWizardRef = React.useRef<HTMLDivElement>(null);
  const mobileWizardWrapper = registerTarget<HTMLDivElement>("WizardWrapperSticky");
  const {
    flightClassCode,
    listOfAdditionalLegs,
    subLOBState,
    nonHotelTravelersMetadata,
    multiCityFlightLegURL,
    lastAdditionalLegDate,
  } = flightWizardState;

  flightWizardState.overrideConfig(() => {
    flightWizardState.config.enableNonStopFlight = true;
  });

  if (listOfAdditionalLegs.length > 1) {
    liveAnnounce(formatText(locKeys.multiCityFlightLegLabel, listOfAdditionalLegs.length + 1), Level.POLITE);
  }

  const onClickScrollToWizard = (): void => {
    scrollTo("WizardWrapperSticky", []);
  };

  const flightHiddenSearchDataProps: FlightHiddenSearchDataProps = {
    flightType: "on",
    flightClass: WizardFlightPWA.getFlightClassCodeForURL(flightClassCode),
    mode: "search",
    trip: subLOBState,
    leg1: WizardFlightPWA.getLegURLFragment(flightWizardState, flightConfig)[0],
    leg2: WizardFlightPWA.getLegURLFragment(flightWizardState, flightConfig)[1],
    flightAirline: globalWizardState.flightAirline,
    flightWizardState: props.wizardState.flightWizardState,
    maxhops: props.wizardState.flightWizardState.isNonStopFlightChecked ? "maxhops:0" : "",
    globalWizardState,
  };

  const handleScroll = () => {
    if (mobileWizardWrapper && mobileWizardWrapper.current) {
      const ctaPosition = mobileWizardWrapper.current.getBoundingClientRect().bottom < 0;
      setCTASticky(ctaPosition);
    } else {
      setCTASticky(false);
    }
  };
  React.useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", () => handleScroll);
    };
  }, []);

  useDidUpdateEffect(() => {
    if (subLOBState === FlightType.ROUND_TRIP) {
      triggerFlightsPredictiveSearchURL({
        flightHiddenSearchDataProps,
        multiCityFlightLegURL,
        travelersMetadata: nonHotelTravelersMetadata,
      });
    }
  }, [flightWizardState.date.end]);

  useDidUpdateEffect(() => {
    if (subLOBState === FlightType.ONE_WAY) {
      triggerFlightsPredictiveSearchURL({
        flightHiddenSearchDataProps,
        multiCityFlightLegURL,
        travelersMetadata: nonHotelTravelersMetadata,
      });
    }
  }, [flightWizardState.date.start]);

  useDidUpdateEffect(() => {
    if (
      checkIfCurrentLastLegDateUpdated(
        subLOBState,
        globalWizardState.getDateFromConfig(flightConfig.date).start,
        lastAdditionalLegDate
      )
    ) {
      triggerFlightsPredictiveSearchURL({
        flightHiddenSearchDataProps,
        multiCityFlightLegURL,
        travelersMetadata: nonHotelTravelersMetadata,
      });
    }
  }, [lastAdditionalLegDate]);

  const stickyWizardEnabled =
    !(flightWizardState.isAddAHotelChecked || flightWizardState.isAddACarChecked) &&
    flightWizardState.subLOBState !== FlightType.MULTI_CITY;

  return (
    <Viewport>
      <ViewSmall>
        <div className="WizardFlightPWALP-Wrapper" id="WizardWrapperSticky" ref={mobileWizardWrapper}>
          <Form flightHiddenSearchDataProps={flightHiddenSearchDataProps} options={options} {...props} />
          <MobileCTA
            view={ViewSize.SMALL}
            ctaVisible={isCTASticky}
            onClickScrollToWizard={onClickScrollToWizard}
            displayText={formatText("wizard.mobile.label.flight")}
          />
        </div>
      </ViewSmall>
      <ViewMedium>
        <div className="WizardFlightPWALP-Wrapper" id="WizardWrapperSticky" ref={stickyWizardRef}>
          {stickyWizardEnabled ? (
            <StackedSticky trigger="bottom" padded>
              <Form flightHiddenSearchDataProps={flightHiddenSearchDataProps} options={options} {...props} />
            </StackedSticky>
          ) : (
            <Form flightHiddenSearchDataProps={flightHiddenSearchDataProps} options={options} {...props} />
          )}
        </div>
      </ViewMedium>
    </Viewport>
  );
});

const Form: React.FC<
  WizardFlightPWAViewProps & {
    flightHiddenSearchDataProps: FlightHiddenSearchDataProps;
    options: WizardFlightPWAFTNodeOptions;
  }
> = (props) => {
  const { wizardState, flightHiddenSearchDataProps, onSelectFlightType, options } = props;

  const { isSticky } = useStickyElement();
  const { formatText } = useLocalization();

  const { flightWizardState, globalWizardState } = wizardState;
  const { config: flightConfig, subLOBState } = flightWizardState;

  const showWhenNotSticky = !isSticky;
  const wizardFormTestId = isSticky ? "WizardFlightPWALP-sticky" : "WizardFlightPWALP";

  const currentTab = subLOBState; // tab state is calculated based on subLOB
  const tabIdPrefix = "wizard-flight-tab-";

  const referrerId =
    currentTab !== FlightType.MULTI_CITY ? flightConfig.referrerId : flightConfig.multiCityFlight.referrerId;

  const { pageHeading, pageSubHeadline } = props.compositionStore.composition!;

  const titleFromComposition = (options.titleFromPageHeader && pageHeading) || undefined;
  const subTitleFromComposition = (options.subTitleFromPageSubHeader && pageSubHeadline) || undefined;

  const travelersColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: 2, large: 2 };
  const preferredClassColSpan: UitkLayoutConditionalGridSpan = { small: 3, medium: 2, large: 2 };
  const { config, isAddAHotelChecked, isAddACarChecked } = flightWizardState;
  const travelerConfig =
    config.enableAddHotel && isAddAHotelChecked
      ? config.travelersWithHotel
      : config.enableAddCar && isAddACarChecked
      ? config.travelersWithCar
      : config.travelers;

  /**
   * Value used if no rehydratation(JS disabled)
   */
  const { noRehydratationValues } = flightConfig.travelers;

  /**
   * Builds the Travelers and PreferredClassInput
   * as a prop to be sent to UitkExperimentalButtonTabs
   * to display them next to the UitkTabs, at the right
   */
  const noNavigationItems = () => (
    <Viewport>
      <ViewLarge>
        <UitkLayoutFlex alignItems="center" space="six" className="all-x-padding-six">
          <UitkLayoutFlexItem>
            <Travelers
              lobState={flightWizardState}
              config={travelerConfig}
              colSpan={travelersColSpan}
              noRehydratationValues={noRehydratationValues}
              triggerType={TravelersTriggerTypes.LINK_BUTTON}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <PreferredClassInput
              colSpan={preferredClassColSpan}
              onSelectPreferredClassCode={props.onSelectPreferredClassCode}
              wizardState={props.wizardState}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <PreferredAirlineInput
              onSelectPreferredAirline={props.onSelectPreferredAirline}
              wizardState={props.wizardState}
            />
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </ViewLarge>
    </Viewport>
  );

  return (
    <form
      noValidate
      className="WizardFlightPWALP"
      action={
        flightWizardState.isAddAHotelChecked || flightWizardState.isAddACarChecked
          ? "/flexibleshopping"
          : "/Flights-Search"
      }
      onSubmit={flightWizardState.submit}
      id={flightConfig.elementId}
      data-fm={options.fmId}
      data-fm-title-id={options.fmTitleId}
      data-testid={wizardFormTestId}
    >
      {flightWizardState.isAddAHotelChecked || flightWizardState.isAddACarChecked ? (
        <FHCHiddenSearchData flightWizardState={flightWizardState} />
      ) : (
        <FlightHiddenSearchData {...flightHiddenSearchDataProps} />
      )}

      {showWhenNotSticky && (
        <>
          <LobTitle lobState={flightWizardState} customTitle={titleFromComposition} />
          <LobSubtitle lobState={flightWizardState} customSubTitle={subTitleFromComposition} />
        </>
      )}
      {/* Each tab represents a flights sub LOB (roundtrip, one way and multicity) */}
      <UitkExperimentalButtonTabs
        className="all-t-margin-three flightTypeTabs"
        selectedTab={currentTab}
        onTabSelect={onSelectFlightType}
        tabsType={UitkTabsType.NATURAL}
        tabAnchorType={UitkTabAnchorType.BUTTON}
        noNavigationItems={noNavigationItems}
        data-testid={currentTab}
      >
        <UitkTab
          name={FlightType.ROUND_TRIP}
          displayName={formatText(locKeys.flightRoundTripText)}
          targetURI={`?flightType=${FlightType.ROUND_TRIP}`}
          id={`${tabIdPrefix}${FlightType.ROUND_TRIP}`}
        >
          {currentTab === FlightType.ROUND_TRIP && (
            <>
              {isSticky ? (
                <StickyRoundtripFlight lobState={flightWizardState} flightConfig={flightConfig} {...props} />
              ) : (
                <RoundtripFlight lobState={flightWizardState} flightConfig={flightConfig} {...props} />
              )}
            </>
          )}
        </UitkTab>

        <UitkTab
          name={FlightType.ONE_WAY}
          displayName={formatText(locKeys.flightOneWayText)}
          targetURI={`?flightType=${FlightType.ONE_WAY}`}
          id={`${tabIdPrefix}${FlightType.ONE_WAY}`}
        >
          {currentTab === FlightType.ONE_WAY && (
            <>
              {isSticky ? (
                <StickyOnewayFlight lobState={flightWizardState} flightConfig={flightConfig} {...props} />
              ) : (
                <OnewayFlight lobState={flightWizardState} flightConfig={flightConfig} {...props} />
              )}
            </>
          )}
        </UitkTab>

        <UitkTab
          name={FlightType.MULTI_CITY}
          displayName={formatText(locKeys.flightMultiCityText)}
          targetURI={`?flightType=${FlightType.MULTI_CITY}`}
          id={`${tabIdPrefix}${FlightType.MULTI_CITY}`}
          className={`${isSticky ? "is-hidden" : ""}`}
        >
          {currentTab === FlightType.MULTI_CITY && (
            <MulticityFlight lobState={flightWizardState} flightConfig={flightConfig} {...props} />
          )}
        </UitkTab>
      </UitkExperimentalButtonTabs>
      <Viewport>
        <ViewSmall>
          <UitkLayoutFlex alignItems="center" space="six" className="all-t-padding-four">
            <UitkLayoutFlexItem>
              <div>
                <Travelers
                  lobState={flightWizardState}
                  config={travelerConfig}
                  colSpan={travelersColSpan}
                  noRehydratationValues={noRehydratationValues}
                  triggerType={TravelersTriggerTypes.LINK_BUTTON}
                />
              </div>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <PreferredClassInput
                colSpan={preferredClassColSpan}
                onSelectPreferredClassCode={props.onSelectPreferredClassCode}
                wizardState={props.wizardState}
              />
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <PreferredAirlineInput
                onSelectPreferredAirline={props.onSelectPreferredAirline}
                wizardState={props.wizardState}
              />
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        </ViewSmall>
        <ViewLarge />
      </Viewport>
      {showWhenNotSticky && (
        <SubmitRow lobState={flightWizardState} rfrr={referrerId} globalConfig={globalWizardState.config} />
      )}
    </form>
  );
};
